import { useEffect, useState } from "react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { subtabAccessibleFor } from "../lib/modules";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Inbox, UserMinus, Laptop, Package, Wallet, IndianRupee, MessageCircle, CalendarClock, Send, FileText, Download, Upload, Trash2, RefreshCw, KeyRound, Eye, EyeOff, Image as ImageIcon } from "lucide-react";
import { PageHeader } from "../components/GradientCard";

import { fmtDateStr } from "../lib/datetime";

const STATUS_CHIP = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
  withdrawn: "bg-slate-200 text-slate-700",
  revoked: "bg-violet-100 text-violet-800",
};

const RATINGS = ["Excellent", "Good", "Average", "Poor"];

// Add `days` calendar days to ISO date string `iso`. Returns YYYY-MM-DD.
const addDays = (iso, days) => {
  try {
    const d = new Date(iso || new Date().toISOString());
    d.setDate(d.getDate() + Number(days || 0));
    return d.toISOString().slice(0, 10);
  } catch { return iso || ""; }
};

const lwdPassed = (r) => {
  const lwd = r?.approved_last_working_day;
  if (!lwd) return false;
  return lwd < new Date().toISOString().slice(0, 10);
};
const SECTIONS = [
  ["Career & Growth", ["primary_reason", "decision_influencer", "growth_adequate", "career_goals_discussed", "learning_opportunities"]],
  ["Manager & Leadership", ["manager_relationship", "manager_feedback_regular", "manager_supportive", "manager_could_do_diff"]],
  ["Compensation & Benefits", ["comp_satisfied", "benefits_satisfied", "comp_major_reason"]],
  ["Work Environment", ["culture_rating", "felt_respected", "policies_fair", "workplace_issues", "had_resources"]],
  ["Team & Collaboration", ["teamwork_rating", "communication_effective", "team_challenges"]],
  ["Employee Experience", ["liked_most", "liked_least", "improvements", "could_have_retained"]],
  ["Future Relationship", ["recommend_to_others", "rejoin_in_future", "open_to_consulting", "future_contact_ok"]],
  ["Final Feedback", ["additional_comments"]],
];
const QUESTION_LABELS = {
  primary_reason: "Primary reason for leaving",
  decision_influencer: "What influenced the decision the most",
  growth_adequate: "Adequate growth opportunities?",
  career_goals_discussed: "Career goals discussed regularly?",
  learning_opportunities: "Sufficient L&D opportunities?",
  manager_relationship: "Rating · relationship with manager",
  manager_feedback_regular: "Regular feedback from manager?",
  manager_supportive: "Felt supported by manager?",
  manager_could_do_diff: "What could the manager have done differently",
  comp_satisfied: "Satisfied with compensation?",
  benefits_satisfied: "Satisfied with benefits?",
  comp_major_reason: "Was compensation a major reason?",
  culture_rating: "Rating · work culture",
  felt_respected: "Felt respected and valued?",
  policies_fair: "Policies fair and transparent?",
  workplace_issues: "Workplace issues experienced",
  had_resources: "Had resources to perform effectively?",
  teamwork_rating: "Rating · teamwork in department",
  communication_effective: "Communication channels effective?",
  team_challenges: "Team challenges faced",
  liked_most: "Liked most",
  liked_least: "Liked least",
  improvements: "Improvements recommended",
  could_have_retained: "What could have retained you",
  recommend_to_others: "Would you recommend to others?",
  rejoin_in_future: "Would you rejoin in future?",
  open_to_consulting: "Open to consulting/freelancing?",
  future_contact_ok: "Can we contact you about future opportunities?",
  additional_comments: "Additional comments or suggestions",
};

export default function Offboarding({ embedded = false } = {}) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const isMaster = user?.role === "master_admin";
  const [topTab, setTopTab] = useState("resignations");
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [viewing, setViewing] = useState(null);
  const [exitView, setExitView] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveForm, setApproveForm] = useState({ approved_notice_days: 30, approved_last_working_day: "", remark: "" });
  const [saving, setSaving] = useState(false);
  // v109.9.143 — Admin-initiated offboarding
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateEmployees, setInitiateEmployees] = useState([]);
  const [initiateForm, setInitiateForm] = useState({
    employee_id: "", reason_category: "", detailed_reason: "",
    serve_notice: true, requested_notice_days: 30,
    proposed_last_working_day: "", no_notice_reason: "",
  });
  const openInitiate = async () => {
    try {
      const { data } = await api.get("/directory");
      setInitiateEmployees(data || []);
    } catch { setInitiateEmployees([]); }
    setInitiateForm({
      employee_id: "", reason_category: "", detailed_reason: "",
      serve_notice: true, requested_notice_days: 30,
      proposed_last_working_day: addDays(new Date().toISOString().slice(0, 10), 30),
      no_notice_reason: "",
    });
    setInitiateOpen(true);
  };
  const doInitiate = async () => {
    if (!initiateForm.employee_id) return toast.error("Pick an employee");
    if (!initiateForm.reason_category) return toast.error("Pick a reason category");
    if ((initiateForm.detailed_reason || "").trim().length < 3) return toast.error("Detailed reason required");
    if (!initiateForm.proposed_last_working_day) return toast.error("Proposed last working day required");
    if (!initiateForm.serve_notice && (initiateForm.no_notice_reason || "").trim().length < 3) return toast.error("Reason for not serving notice required");
    setSaving(true);
    try {
      await api.post("/resignations/admin-initiate", initiateForm);
      toast.success("Offboarding initiated");
      setInitiateOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const load = () => {
    api.get(`/resignations${filter ? `?status=${filter}` : ""}`)
      .then(({ data }) => setList(data || []))
      .catch(() => setList([]));
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openApprove = (r) => {
    const base = r.date_of_submission || new Date().toISOString().slice(0, 10);
    const days = r.requested_notice_days || 30;
    const lwd = addDays(base, days);
    setApproveForm({
      approved_notice_days: days,
      approved_last_working_day: lwd,
      remark: "",
    });
    setApproveOpen(true);
  };
  const doApprove = async () => {
    setSaving(true);
    try {
      await api.post(`/resignations/${viewing.id}/approve`, {
        approved_notice_days: Number(approveForm.approved_notice_days),
        approved_last_working_day: approveForm.approved_last_working_day,
        remark: approveForm.remark,
      });
      toast.success("Resignation approved");
      setApproveOpen(false); setViewing(null); load();
    } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); }
  };
  const doReject = async () => {
    const remark = window.prompt("Optional rejection note:", "");
    if (remark === null) return;
    try {
      await api.post(`/resignations/${viewing.id}/reject`, { remark });
      toast.success("Resignation rejected");
      setViewing(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const doRevoke = async () => {
    const remark = window.prompt(
      "Revoke this resignation? The employee will be restored to active workforce.\n\nOptional reason for revoke:",
      "",
    );
    if (remark === null) return;
    try {
      await api.post(`/resignations/${viewing.id}/revoke`, { remark });
      toast.success("Resignation revoked — employee restored");
      setViewing(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const counts = list.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});

  return (
    <div className={embedded ? "space-y-6" : "max-w-[1400px] space-y-6"}>
      {!embedded && (
        <PageHeader
          moduleKey="hr"
          tone="rose"
          icon={UserMinus}
          eyebrow="HR Module"
          title="Offboarding"
          subtitle="All resignation requests, exit interviews and approvals."
        />
      )}

      <Tabs value={topTab} onValueChange={setTopTab} data-testid="offb-top-tabs">
        <TabsList>
          {subtabAccessibleFor(user, "hr", "offboarding", "resignations") && (
            <TabsTrigger value="resignations" data-testid="offb-top-tab-resignations">Resignations</TabsTrigger>
          )}
          {subtabAccessibleFor(user, "hr", "offboarding", "fnf") && (
            <TabsTrigger value="fnf" data-testid="offb-top-tab-fnf">FNF Dashboard</TabsTrigger>
          )}
          {subtabAccessibleFor(user, "hr", "offboarding", "resigned") && (
            <TabsTrigger value="resigned" data-testid="offb-top-tab-resigned">Resigned Employees</TabsTrigger>
          )}
          {subtabAccessibleFor(user, "hr", "offboarding", "letterhead") && (
            <TabsTrigger value="letterhead" data-testid="offb-top-tab-letterhead">Letterhead Settings</TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {topTab === "fnf" ? (
        <FNFDashboard />
      ) : topTab === "resigned" ? (
        <ResignedEmployeesPanel />
      ) : topTab === "letterhead" ? (
        <LetterheadPanel isMaster={isMaster} />
      ) : (
      <>
      <div className="flex items-center gap-2 flex-wrap">
        <Tabs value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)} className="flex-1">
          <TabsList>
            {["all", "pending", "approved", "rejected", "withdrawn", "revoked"].map((s) => (
              <TabsTrigger key={s} value={s} data-testid={`offb-filter-${s}`}>
                <span className="capitalize">{s}</span>
                {s !== "all" && counts[s] != null && <span className="ml-1 opacity-70 text-[10px]">({counts[s] || 0})</span>}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {/* v109.9.143 — Admin-initiated offboarding trigger */}
        {(isMaster || user?.role === "hr_admin") && (
          <Button
            onClick={openInitiate}
            className="bg-rose-600 hover:bg-rose-700 text-white h-9 shadow-sm"
            data-testid="offb-initiate-btn"
          >
            <UserMinus size={15} className="mr-1.5" /> Initiate Offboarding
          </Button>
        )}
      </div>

      <div className="pk-card overflow-hidden">
        {viewMode === "card" ? (
          list.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm flex flex-col items-center gap-2"><Inbox size={22} /> No resignations.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="offb-cards">
              {list.map((r) => (
                <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`offb-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-800 truncate">{r.employee_name}</div>
                      <div className="text-[10px] text-slate-400 mono truncate">{r.employee_code} · {r.employment_type || "—"} · {r.job_type || "—"}</div>
                    </div>
                    <Badge className={(STATUS_CHIP[r.status] || "bg-slate-100") + " shrink-0"}>{r.status}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Submitted</div><div className="mono text-slate-700">{fmtDateStr(r.date_of_submission)}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Reason</div><div className="text-slate-800 truncate" title={r.detailed_reason}>{r.reason_category}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Notice</div><div className="text-slate-800">{r.serve_notice ? <>{r.requested_notice_days}d req{r.approved_notice_days > 0 ? <span className="ml-1 text-emerald-700 font-semibold">· {r.approved_notice_days}d ok</span> : ""}</> : <span className="text-rose-700 font-semibold">No notice</span>}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Last Day</div><div className="mono text-slate-700 truncate">{fmtDateStr(r.proposed_last_working_day)}</div></div>
                  </div>
                  {r.exit_interview_submitted && <div className="text-[10px] text-violet-700 mb-2">Exit interview ✓</div>}
                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
                    {r.exit_interview_submitted && (
                      <Button size="sm" variant="outline" onClick={() => setExitView(r)} className="h-7 text-[10.5px] text-violet-700 border-violet-300 bg-violet-50 hover:bg-violet-100" data-testid={`offb-exit-view-${r.id}`}>
                        <MessageCircle size={10} className="mr-1" /> Exit
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setViewing(r)} className="h-7 text-[10.5px]" data-testid={`offb-view-${r.id}`}>View</Button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/60 border-b border-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 font-semibold">Submitted</th>
                <th className="px-4 py-3 font-semibold">Reason</th>
                <th className="px-4 py-3 font-semibold">Notice</th>
                <th className="px-4 py-3 font-semibold">Last Working Day</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm"><Inbox className="mx-auto mb-2" size={20} /> No resignations.</td></tr>
              )}
              {list.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`offb-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{r.employee_name}</div>
                    <div className="text-[10px] text-slate-400 mono">{r.employee_code} · {r.employment_type || "—"} · {r.job_type || "—"}</div>
                  </td>
                  <td className="px-4 py-3 mono text-[11px] text-slate-600">{fmtDateStr(r.date_of_submission)}</td>
                  <td className="px-4 py-3 text-[12px] max-w-xs truncate" title={r.detailed_reason}>{r.reason_category}</td>
                  <td className="px-4 py-3 text-[11.5px]">
                    {r.serve_notice ? (
                      <>
                        <div className="mono">{r.requested_notice_days}d requested</div>
                        {r.approved_notice_days > 0 && <div className="mono text-emerald-700 font-semibold">{r.approved_notice_days}d approved</div>}
                      </>
                    ) : <span className="text-rose-700 font-semibold">No notice</span>}
                  </td>
                  <td className="px-4 py-3 mono text-[11.5px]">
                    <div className="text-slate-500">prop · {fmtDateStr(r.proposed_last_working_day)}</div>
                    {r.approved_last_working_day && (
                      <InlineLWDEditor
                        resignation={r}
                        canEdit={r.status === "approved"}
                        onSaved={load}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={STATUS_CHIP[r.status] || "bg-slate-100"}>{r.status}</Badge>
                    {r.exit_interview_submitted && <div className="text-[9.5px] text-violet-700 mt-1">Exit interview ✓</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.exit_interview_submitted && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setExitView(r)}
                        className="h-8 text-[11px] mr-1.5 text-violet-700 border-violet-300 bg-violet-50 hover:bg-violet-100 dark:text-violet-300 dark:border-violet-800 dark:bg-violet-500/10 dark:hover:bg-violet-500/20"
                        data-testid={`offb-exit-view-${r.id}`}
                      >
                        <MessageCircle size={11} className="mr-1" /> Exit Interview
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setViewing(r)} className="h-8 text-[11px]" data-testid={`offb-view-${r.id}`}>View</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Detail dialog */}
      </>
      )}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {viewing.employee_name} <Badge className={STATUS_CHIP[viewing.status]}>{viewing.status}</Badge>
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid grid-cols-3 mb-3">
                  <TabsTrigger value="details" data-testid="offb-tab-details">Details &amp; Exit Interview</TabsTrigger>
                  <TabsTrigger value="fnf" data-testid="offb-tab-fnf" disabled={viewing.status !== "approved"}>
                    Assets &amp; FNF
                  </TabsTrigger>
                  <TabsTrigger value="docs" data-testid="offb-tab-docs" disabled={viewing.status !== "approved"}>
                    Documents
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details">
                  <DetailsPanel viewing={viewing} />
                </TabsContent>
                <TabsContent value="fnf">
                  <FNFPanel resignationId={viewing.id} onSaved={() => { load(); }} />
                </TabsContent>
                <TabsContent value="docs">
                  <DocumentsPanel resignationId={viewing.id} employeeName={viewing.employee_name} />
                </TabsContent>
              </Tabs>

              <DialogFooter className="mt-4 gap-2 flex-wrap">
                {viewing.status === "pending" && (
                  <>
                    <Button variant="outline" onClick={doReject} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="offb-reject">Reject</Button>
                    <Button onClick={() => openApprove(viewing)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="offb-approve-btn">Approve &amp; Set Last Working Day</Button>
                  </>
                )}
                {isMaster && !["revoked", "withdrawn"].includes(viewing.status) && !lwdPassed(viewing) && (
                  <Button variant="outline" onClick={doRevoke} className="text-violet-700 border-violet-300 hover:bg-violet-50" data-testid="offb-revoke">
                    Revoke Resignation
                  </Button>
                )}
                {isMaster && lwdPassed(viewing) && (
                  <Badge className="bg-slate-100 text-slate-600 self-center text-[10px]" data-testid="offb-revoke-blocked">Last working day has passed — revoke not allowed</Badge>
                )}
                <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dedicated Exit Interview viewer — clean, full-text, dark-mode safe */}
      <Dialog open={!!exitView} onOpenChange={(o) => { if (!o) setExitView(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="offb-exit-interview-dialog">
          {exitView && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageCircle size={16} className="text-violet-600 dark:text-violet-300" />
                  Exit Interview — {exitView.employee_name}
                </DialogTitle>
              </DialogHeader>

              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge className="bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300" data-keep-color>
                  Submitted{exitView.exit_interview_submitted_at ? ` · ${new Date(exitView.exit_interview_submitted_at).toLocaleDateString("en-IN")}` : ""}
                </Badge>
                <Badge className={`${STATUS_CHIP[exitView.status] || ""}`} data-keep-color>{exitView.status}</Badge>
                {exitView.designation && <span className="text-[11px] text-slate-500 dark:text-slate-400">{exitView.designation}</span>}
              </div>

              {Object.keys(exitView.exit_interview || {}).length === 0 ? (
                <div className="text-[12px] italic text-slate-400 dark:text-slate-500 px-3 py-6 text-center bg-slate-50 dark:bg-[hsl(var(--muted))] rounded-lg border border-dashed border-slate-200 dark:border-[hsl(var(--border))]">
                  Exit interview has not been submitted yet.
                </div>
              ) : (
                <div className="space-y-5">
                  {SECTIONS.map(([title, keys]) => (
                    <section key={title} data-testid={`offb-exit-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <div className="text-[10.5px] uppercase tracking-[0.15em] font-extrabold text-violet-700 dark:text-violet-300 mb-2 flex items-center gap-2">
                        <span className="w-1 h-4 rounded-full bg-violet-500 dark:bg-violet-400" data-keep-color /> {title}
                      </div>
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        {keys.map((k) => {
                          const ans = (exitView.exit_interview?.[k] ?? "").toString();
                          const empty = !ans.trim();
                          return (
                            <div
                              key={k}
                              className="rounded-lg border border-slate-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card))] p-2.5"
                            >
                              <dt className="text-[10.5px] text-slate-500 dark:text-slate-400 font-semibold mb-1">
                                {QUESTION_LABELS[k] || k}
                              </dt>
                              <dd
                                className={`text-[12.5px] whitespace-pre-wrap break-words ${
                                  empty
                                    ? "text-slate-400 dark:text-slate-500 italic"
                                    : "text-slate-800 dark:text-slate-100 font-medium"
                                }`}
                              >
                                {empty ? "— No response —" : ans}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    </section>
                  ))}
                </div>
              )}

              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    try {
                      const lines = [];
                      lines.push(`Exit Interview — ${exitView.employee_name}`);
                      if (exitView.exit_interview_submitted_at) lines.push(`Submitted: ${new Date(exitView.exit_interview_submitted_at).toLocaleString("en-IN")}`);
                      lines.push("");
                      for (const [title, keys] of SECTIONS) {
                        lines.push(`== ${title} ==`);
                        for (const k of keys) {
                          lines.push(`${QUESTION_LABELS[k] || k}: ${exitView.exit_interview?.[k] || "—"}`);
                        }
                        lines.push("");
                      }
                      navigator.clipboard.writeText(lines.join("\n"));
                      toast.success("Exit interview copied to clipboard");
                    } catch (e) { toast.error("Copy failed"); }
                  }}
                  data-testid="offb-exit-copy"
                >
                  Copy as Text
                </Button>
                <Button variant="outline" onClick={() => setExitView(null)} data-testid="offb-exit-close">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Approve Resignation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Approved Notice Days *</Label>
              <Input
                type="number"
                min="0"
                value={approveForm.approved_notice_days}
                onChange={(e) => {
                  const days = e.target.value;
                  // Recompute Last Working Day = date_of_submission + days.
                  const base = viewing?.date_of_submission || new Date().toISOString().slice(0, 10);
                  setApproveForm({
                    ...approveForm,
                    approved_notice_days: days,
                    approved_last_working_day: addDays(base, days),
                  });
                }}
                className="mt-1.5 mono"
                data-testid="offb-approved-days"
              />
              <div className="text-[10.5px] text-slate-500 mt-1">
                Last working day auto-updates from <span className="mono">{viewing?.date_of_submission || "today"}</span> + notice days.
              </div>
            </div>
            <div>
              <Label>Last Working Day *</Label>
              <Input type="date" value={approveForm.approved_last_working_day || ""} onChange={(e) => setApproveForm({ ...approveForm, approved_last_working_day: e.target.value })} className="mt-1.5" data-testid="offb-approved-lwd" />
              <div className="text-[10.5px] text-slate-500 mt-1">This will be the employee&apos;s final date with the company. You can override manually if needed.</div>
            </div>
            <div>
              <Label>Remark (optional)</Label>
              <Textarea rows={2} value={approveForm.remark} onChange={(e) => setApproveForm({ ...approveForm, remark: e.target.value })} className="mt-1.5" data-testid="offb-approve-remark" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={doApprove} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="offb-approve-confirm">{saving ? "Approving…" : "Approve"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.9.143 — Admin-initiated offboarding dialog */}
      <Dialog open={initiateOpen} onOpenChange={setInitiateOpen}>
        <DialogContent className="max-w-lg" data-testid="offb-initiate-dialog">
          <DialogHeader><DialogTitle>Initiate Offboarding (Admin)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee <span className="text-rose-500">*</span></Label>
              <Select value={initiateForm.employee_id} onValueChange={(v) => setInitiateForm({ ...initiateForm, employee_id: v })}>
                <SelectTrigger data-testid="offb-initiate-emp"><SelectValue placeholder="Pick an active employee…" /></SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {initiateEmployees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code || "—"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason Category <span className="text-rose-500">*</span></Label>
              <Select value={initiateForm.reason_category} onValueChange={(v) => setInitiateForm({ ...initiateForm, reason_category: v })}>
                <SelectTrigger data-testid="offb-initiate-reason"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {["Better Career Opportunity","Higher Education","Relocation","Personal Reasons","Family Reasons","Compensation & Benefits","Work Environment","Management Issues","Health Reasons","Entrepreneurship","Other"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Detailed Reason <span className="text-rose-500">*</span></Label>
              <Textarea rows={3} value={initiateForm.detailed_reason}
                onChange={(e) => setInitiateForm({ ...initiateForm, detailed_reason: e.target.value })}
                placeholder="Context / narrative for this offboarding" data-testid="offb-initiate-detail" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={initiateForm.serve_notice}
                onCheckedChange={(v) => setInitiateForm({ ...initiateForm, serve_notice: !!v })}
                data-testid="offb-initiate-serve-notice" />
              <span className="text-[12px] text-slate-700 dark:text-slate-200">Will serve notice period</span>
            </div>
            {initiateForm.serve_notice ? (
              <div>
                <Label>Notice Days</Label>
                <Input type="number" min="1" value={initiateForm.requested_notice_days}
                  onChange={(e) => setInitiateForm({ ...initiateForm, requested_notice_days: Number(e.target.value) || 0 })}
                  data-testid="offb-initiate-notice-days" />
              </div>
            ) : (
              <div>
                <Label>Reason for NOT serving notice <span className="text-rose-500">*</span></Label>
                <Input value={initiateForm.no_notice_reason}
                  onChange={(e) => setInitiateForm({ ...initiateForm, no_notice_reason: e.target.value })}
                  data-testid="offb-initiate-no-notice-reason" />
              </div>
            )}
            <div>
              <Label>Proposed Last Working Day <span className="text-rose-500">*</span></Label>
              <Input type="date" value={initiateForm.proposed_last_working_day}
                onChange={(e) => setInitiateForm({ ...initiateForm, proposed_last_working_day: e.target.value })}
                data-testid="offb-initiate-lwd" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInitiateOpen(false)}>Cancel</Button>
            <Button onClick={doInitiate} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="offb-initiate-submit">
              {saving ? "Initiating…" : "Initiate Offboarding"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className={`text-slate-800 mt-0.5 ${mono ? "mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}

function InlineLWDEditor({ resignation, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(resignation.approved_last_working_day || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!value) { toast.error("Date required"); return; }
    setSaving(true);
    try {
      await api.patch(`/resignations/${resignation.id}`, { approved_last_working_day: value });
      toast.success("Last working day updated");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); }
  };
  if (!canEdit) return <div className="text-emerald-700 font-semibold">final · {fmtDateStr(resignation.approved_last_working_day)}</div>;
  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-emerald-700 font-semibold">final · {fmtDateStr(resignation.approved_last_working_day)}</span>
        <button onClick={() => setEditing(true)} className="text-[10px] text-violet-700 hover:underline" data-testid={`offb-edit-lwd-${resignation.id}`}>Edit</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input type="date" value={value} onChange={(e) => setValue(e.target.value)} className="h-7 w-32 mono text-[11px]" data-testid={`offb-lwd-input-${resignation.id}`} />
      <Button size="sm" onClick={save} disabled={saving} className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" data-testid={`offb-lwd-save-${resignation.id}`}>Save</Button>
      <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setValue(resignation.approved_last_working_day || ""); }} className="h-7 text-[10px]">×</Button>
    </div>
  );
}

function DetailsPanel({ viewing }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px] mb-4">
        <KV k="Employee Code" v={viewing.employee_code} mono />
        <KV k="Date of Joining" v={fmtDateStr(viewing.date_of_joining)} mono />
        <KV k="Employment Type" v={viewing.employment_type} />
        <KV k="Job Type" v={viewing.job_type} />
        <KV k="Date of Submission" v={fmtDateStr(viewing.date_of_submission)} mono />
        <KV k="Proposed Last Working Day" v={fmtDateStr(viewing.proposed_last_working_day)} mono />
        <KV k="Reason" v={viewing.reason_category} />
        <KV k="Notice" v={viewing.serve_notice ? `${viewing.requested_notice_days}d (requested)` : `No notice — ${viewing.no_notice_reason}`} />
        {viewing.approved_notice_days > 0 && <KV k="Approved Notice" v={`${viewing.approved_notice_days}d`} mono />}
        {viewing.approved_last_working_day && <KV k="Approved Last Working Day" v={fmtDateStr(viewing.approved_last_working_day)} mono />}
      </div>
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Detailed Reason</div>
        <div className="text-slate-700 mt-1 text-[13px] bg-slate-50 p-3 rounded">{viewing.detailed_reason}</div>
      </div>
      {viewing.decision_remark && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Admin Decision Note</div>
          <div className="text-slate-700 mt-1 italic">{viewing.decision_remark}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">— {viewing.approved_by_name}</div>
        </div>
      )}
      {viewing.exit_interview_submitted && Object.keys(viewing.exit_interview || {}).length > 0 ? (
        <div className="border-t pt-4 mt-2">
          <div className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mb-3">Exit Interview Responses</div>
          <div className="space-y-3">
            {SECTIONS.map(([title, keys]) => (
              <div key={title}>
                <div className="text-[10.5px] uppercase font-semibold text-slate-500 tracking-wider">{title}</div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-1.5 text-[12px]">
                  {keys.map((k) => (
                    <div key={k} className="flex justify-between gap-3 border-b border-slate-100 pb-1">
                      <dt className="text-slate-500 truncate" title={QUESTION_LABELS[k]}>{QUESTION_LABELS[k] || k}</dt>
                      <dd className="text-slate-800 text-right max-w-[55%] truncate" title={viewing.exit_interview[k]}>{viewing.exit_interview[k] || "—"}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="border-t pt-3 mt-2 text-[12px] text-slate-400 italic">Exit interview not submitted yet.</div>
      )}
    </>
  );
}

const CHECKLIST_LABELS = {
  knowledge_transfer_completed: "Knowledge Transfer Completed",
  client_handover_completed: "Client Handover Completed",
  laptop_returned: "Laptop Returned",
  id_card_returned: "ID Card Returned",
  sim_card_returned: "SIM Card Returned",
  email_access_disabled: "Email Access Disabled",
  software_licenses_revoked: "Software Licenses Revoked",
  expense_settlement_completed: "Expense Settlement Completed",
  advance_settlement_completed: "Advance Settlement Completed",
  full_final_settlement_processed: "Full & Final Settlement Processed",
  experience_letter_issued: "Experience Letter Issued",
  relieving_letter_issued: "Relieving Letter Issued",
};
const CHECKLIST_KEYS = Object.keys(CHECKLIST_LABELS);

function FNFPanel({ resignationId, onSaved }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fnfAmount, setFnfAmount] = useState("");
  const [fnfRemark, setFnfRemark] = useState("");
  const [outstandingAdv, setOutstandingAdv] = useState("");
  const [outstandingAdvRemark, setOutstandingAdvRemark] = useState("");
  const [savingAdv, setSavingAdv] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [employees, setEmployees] = useState([]);
  const [replacementId, setReplacementId] = useState("");
  const [logs, setLogs] = useState([]);

  const checklistComplete = CHECKLIST_KEYS.every((k) => !!checklist[k]);
  const pendingCount = CHECKLIST_KEYS.filter((k) => !checklist[k]).length;

  const load = () => {
    setLoading(true);
    api.get(`/resignations/${resignationId}/fnf-summary`)
      .then(({ data }) => {
        setS(data);
        setFnfAmount(data.fnf_amount ? String(data.fnf_amount) : String(data.fnf_estimate.toFixed(2)));
        setFnfRemark(data.fnf_remark || "");
        // Prefill outstanding advance: prefer stored value; else use auto-fetched total
        if (data.outstanding_advance) {
          setOutstandingAdv(String(data.outstanding_advance));
        } else if (data.outstanding_advance_auto) {
          setOutstandingAdv(String(data.outstanding_advance_auto));
        } else {
          setOutstandingAdv("");
        }
        setOutstandingAdvRemark(data.outstanding_advance_remark || "");
        setChecklist(data.exit_checklist || {});
        setReplacementId(data.replacement_employee_id || "");
      })
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
    api.get(`/resignations/${resignationId}/reassignment-logs`).then(({ data }) => setLogs(data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/directory").then(({ data }) => setEmployees(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resignationId]);

  const toggleChecklist = async (k) => {
    const next = { ...checklist, [k]: !checklist[k] };
    setChecklist(next);
    try {
      await api.patch(`/resignations/${resignationId}`, { exit_checklist: next });
    } catch (e) { toast.error(formatError(e)); }
  };

  const reassignClients = async () => {
    if (!replacementId) { toast.error("Pick a replacement employee first"); return; }
    if (!window.confirm("Reassign all CRM clients and link orders of this employee to the replacement?")) return;
    try {
      // First save replacement on the resignation
      await api.patch(`/resignations/${resignationId}`, { replacement_employee_id: replacementId, replacement_employee_name: (employees.find((e) => e.id === replacementId) || {}).name || "" });
      const { data } = await api.post(`/resignations/${resignationId}/reassign-clients`, { replacement_employee_id: replacementId });
      toast.success(`Reassigned ${data.client_count} client(s) and logged ${data.order_count} order(s)`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveFnf = async (finalize) => {
    setFinalizing(true);
    try {
      await api.patch(`/resignations/${resignationId}`, {
        fnf_amount: Number(fnfAmount) || 0,
        fnf_remark: fnfRemark,
        fnf_finalized: finalize,
      });
      toast.success(finalize ? "FNF finalized" : "FNF saved");
      load(); onSaved?.();
    } catch (e) { toast.error(formatError(e)); } finally { setFinalizing(false); }
  };

  const saveOutstanding = async () => {
    setSavingAdv(true);
    try {
      await api.patch(`/resignations/${resignationId}`, {
        outstanding_advance: Number(outstandingAdv) || 0,
        outstanding_advance_remark: outstandingAdvRemark,
      });
      toast.success("Outstanding advance saved");
      load(); onSaved?.();
    } catch (e) { toast.error(formatError(e)); } finally { setSavingAdv(false); }
  };

  if (loading) return <div className="text-slate-400 text-sm py-6 text-center">Loading FNF summary…</div>;
  if (!s) return null;
  const INR = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatBox label="Pending Incentive" value={INR(s.pending_incentive_total)} tone="violet" icon={Wallet} count={s.pending_incentives.length} />
        <StatBox label="Pending Salary" value={INR(s.pending_payroll_total)} tone="sky" icon={IndianRupee} count={s.pending_payroll.length} />
        <StatBox label="Total Deduction" value={INR(s.total_deduction)} tone="rose" icon={Package} />
        <StatBox label="Outstanding Advance" value={INR(s.outstanding_advance)} tone="amber" icon={Wallet} />
        <StatBox label="FNF Estimate" value={INR(s.fnf_estimate)} tone="emerald" icon={Wallet} />
      </div>

      {/* Assets */}
      {s.assigned_assets.length > 0 && (
        <Section title={`Assigned IT Assets (${s.assigned_assets.length})`} icon={Laptop}>
          <ul className="divide-y divide-slate-100 text-[12.5px]">
            {s.assigned_assets.map((a) => (
              <li key={a.id} className="py-1.5 flex justify-between gap-2" data-testid={`fnf-asset-${a.id}`}>
                <div>
                  <div className="font-semibold text-slate-800">{a.make_model || a.asset_type}</div>
                  <div className="mono text-[10.5px] text-slate-500">TAG {a.asset_tag}{a.serial_no && ` · S/N ${a.serial_no}`}</div>
                </div>
                <Badge className="bg-blue-100 text-blue-700 self-start">Assigned</Badge>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Issuances */}
      {s.active_issuances.length > 0 && (
        <Section title={`Active Product Issuances (${s.active_issuances.length})`} icon={Package}>
          <ul className="divide-y divide-slate-100 text-[12.5px]">
            {s.active_issuances.map((iss) => (
              <li key={iss.id} className="py-1.5" data-testid={`fnf-issuance-${iss.id}`}>
                <div className="flex justify-between gap-2 mb-1">
                  <div className="mono font-bold text-slate-800">{iss.issuance_no}</div>
                  <Badge className="bg-sky-100 text-sky-800 self-start">{iss.status}</Badge>
                </div>
                <ul className="ml-3 list-disc text-slate-600 text-[11.5px]">
                  {(iss.items || []).map((line, i) => (
                    <li key={i}>{line.inventory_name} <span className="mono text-slate-500">× {line.qty_issued}</span></li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Incentives */}
      {s.pending_incentives.length > 0 && (
        <Section title="Pending Incentives" icon={Wallet}>
          <ul className="divide-y divide-slate-100 text-[12.5px]">
            {s.pending_incentives.map((i) => (
              <li key={i.id} className="py-1.5 flex justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-800">{i.client_name}</div>
                  <div className="text-[10.5px] text-slate-500">{i.created_at?.slice(0, 10)}</div>
                </div>
                <div className="text-right">
                  <div className="mono font-semibold text-violet-700">{INR(i.incentive_amount)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{i.status}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Payroll */}
      {s.pending_payroll.length > 0 && (
        <Section title="Pending Salary Disbursement" icon={IndianRupee}>
          <ul className="divide-y divide-slate-100 text-[12.5px]">
            {s.pending_payroll.map((p) => (
              <li key={p.id} className="py-1.5 flex justify-between gap-2">
                <div className="mono">{p.month}</div>
                <div className="text-right">
                  <div className="mono font-semibold text-sky-700">{INR(p.amount)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{p.status || "Pending"}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* HR Exit Clearance Checklist — must be complete before Finalize/Disburse */}
      <Section title={`HR Exit Clearance Checklist · ${CHECKLIST_KEYS.length - pendingCount}/${CHECKLIST_KEYS.length}`} icon={Wallet}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {CHECKLIST_KEYS.map((k) => (
            <label key={k} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-[12.5px] ${checklist[k] ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-slate-200 text-slate-700 hover:border-violet-300"}`} data-testid={`checklist-${k}`}>
              <input type="checkbox" checked={!!checklist[k]} onChange={() => toggleChecklist(k)} disabled={s.fnf_disbursed} className="rounded" />
              <span className="flex-1">{CHECKLIST_LABELS[k]}</span>
              {checklist[k] && <span className="text-emerald-600">✓</span>}
            </label>
          ))}
        </div>
        {!checklistComplete && (
          <div className="text-[11.5px] text-amber-700 mt-2 font-semibold">
            {pendingCount} item{pendingCount === 1 ? "" : "s"} pending — FNF cannot be finalized or disbursed until all are complete.
          </div>
        )}
      </Section>

      {/* Outstanding Advance — auto-fetched from Travel + Salary Advances; HR/Master can override */}
      <Section title="Outstanding Advance / Recoverable Amount" icon={Wallet}>
        <div className="text-[11.5px] text-slate-600 mb-2">
          Any unsettled advance taken by the employee (travel or salary advance) that needs to be
          recovered before disbursing FNF. Auto-deducted from the FNF estimate above.
        </div>

        {/* Breakdown card (auto-fetched from dashboard sources) */}
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 mb-3 text-[11.5px]" data-testid="oa-breakdown">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-white border border-amber-300 text-amber-800">
                Travel: {INR(s.outstanding_advance_breakdown?.travel_outstanding || 0)}
              </Badge>
              <span className="text-slate-400">+</span>
              <Badge className="bg-white border border-amber-300 text-amber-800">
                Salary: {INR(s.outstanding_advance_breakdown?.salary_outstanding || 0)}
              </Badge>
              <span className="text-slate-400">=</span>
              <Badge className="bg-amber-600 text-white border border-amber-700 font-bold">
                Auto Total: {INR(s.outstanding_advance_auto || 0)}
              </Badge>
            </div>
            {(s.outstanding_advance_auto || 0) > 0 && Number(outstandingAdv || 0) !== (s.outstanding_advance_auto || 0) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setOutstandingAdv(String(s.outstanding_advance_auto || 0))}
                className="h-7 text-[10.5px] border-amber-400 text-amber-800 hover:bg-amber-100"
                data-testid="oa-use-auto"
              >
                Use auto value
              </Button>
            )}
          </div>
          <div className="text-[10px] text-amber-700 mt-1.5 italic">
            Auto values are derived from approved Travel Advances (minus Claims) and active Salary Advances.
            HR / Master Admin can override the value below if a manual adjustment is needed.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Outstanding Advance (₹)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={outstandingAdv}
              onChange={(e) => setOutstandingAdv(e.target.value)}
              className="mt-1.5 mono font-semibold"
              data-testid="outstanding-advance-input"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Remark / Notes</Label>
            <Input
              value={outstandingAdvRemark}
              onChange={(e) => setOutstandingAdvRemark(e.target.value)}
              placeholder="e.g. Travel advance June 2026 — unsettled"
              className="mt-1.5"
              data-testid="outstanding-advance-remark"
            />
          </div>
        </div>
        <div className="flex justify-end mt-2">
          <Button
            size="sm"
            onClick={saveOutstanding}
            disabled={savingAdv}
            className="bg-amber-600 hover:bg-amber-700 h-8 text-[11px]"
            data-testid="outstanding-advance-save"
          >
            {savingAdv ? "Saving…" : "Save Outstanding Advance"}
          </Button>
        </div>
      </Section>

      {/* Replacement Employee + reassignment */}
      <Section title="Replacement Employee" icon={UserMinus}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Pick the employee taking over</Label>
            <select
              value={replacementId}
              onChange={(e) => setReplacementId(e.target.value)}
              className="mt-1.5 w-full h-10 border border-slate-200 rounded px-2 text-[13px] bg-white"
              data-testid="repl-picker"
            >
              <option value="">— None —</option>
              {employees.filter((e) => e.id !== s.employee_id).map((e) => (
                <option key={e.id} value={e.id}>{e.name} · {e.employee_code} · {e.department}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={reassignClients} disabled={!replacementId} className="bg-violet-600 hover:bg-violet-700 w-full" data-testid="repl-reassign">
              Reassign Clients &amp; Orders
            </Button>
          </div>
        </div>
        {logs.length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-2">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Reassignment Audit Trail</div>
            <ul className="space-y-1.5 text-[11.5px]">
              {logs.map((lg) => (
                <li key={lg.id} className="flex items-start justify-between gap-2 border-b border-slate-100 pb-1.5">
                  <div className="flex-1">
                    <div>
                      <span className="font-semibold text-slate-700">{lg.from_employee_name}</span> → <span className="font-semibold text-violet-700">{lg.to_employee_name}</span>
                      <span className="ml-2 mono text-[10px] text-slate-400">{lg.at.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-[10.5px] text-slate-500 mt-0.5">
                      {lg.items.length} item{lg.items.length === 1 ? "" : "s"} · by {lg.by_name}
                    </div>
                    <details className="mt-1 text-[10.5px] text-slate-600">
                      <summary className="cursor-pointer hover:text-violet-700">View items</summary>
                      <ul className="ml-3 mt-1 list-disc">
                        {lg.items.slice(0, 30).map((it, i) => (
                          <li key={i}><span className="mono text-[10px]">[{it.type}]</span> {it.name || it.id}</li>
                        ))}
                        {lg.items.length > 30 && <li className="text-slate-400 italic">+ {lg.items.length - 30} more…</li>}
                      </ul>
                    </details>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {/* Final FNF editor + disbursement */}
      <Section title="Final FNF Settlement & Disbursement" icon={Wallet}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Final FNF Amount (₹) *</Label>
            <Input type="number" min="0" step="0.01" value={fnfAmount} onChange={(e) => setFnfAmount(e.target.value)} className="mt-1.5 mono font-semibold" disabled={s.fnf_disbursed} data-testid="fnf-amount" />
            <div className="text-[10.5px] text-slate-500 mt-1">Auto-suggested: {INR(s.fnf_estimate)}</div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Remark</Label>
            <Textarea rows={2} value={fnfRemark} onChange={(e) => setFnfRemark(e.target.value)} className="mt-1.5" placeholder="Computation notes, adjustments, etc…" disabled={s.fnf_disbursed} data-testid="fnf-remark" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 border-t border-slate-100 pt-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Planned Disbursement Date</Label>
            <Input type="date" value={s.fnf_disburse_planned_date || ""} onChange={async (e) => {
              try { await api.patch(`/resignations/${resignationId}`, { fnf_disburse_planned_date: e.target.value }); load(); onSaved?.(); }
              catch (err) { toast.error(formatError(err)); }
            }} className="mt-1.5 mono" disabled={s.fnf_disbursed} data-testid="fnf-planned-date" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Disbursement Status</Label>
            <div className="mt-1.5">
              {s.fnf_disbursed ? (
                <Badge className="bg-emerald-100 text-emerald-800 text-[12px] px-3 py-1.5">Disbursed · {s.fnf_disbursed_date || ""}</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 text-[12px] px-3 py-1.5">Pending Disbursement</Badge>
              )}
            </div>
          </div>
          <div className="flex items-end">
            {!s.fnf_disbursed ? (
              <Button onClick={async () => {
                if (!checklistComplete) { toast.error("Complete the HR Exit Clearance Checklist first."); return; }
                if (!window.confirm(`Mark FNF of ${INR(Number(fnfAmount) || 0)} as Disbursed?`)) return;
                try {
                  await api.patch(`/resignations/${resignationId}`, {
                    fnf_amount: Number(fnfAmount) || 0,
                    fnf_remark: fnfRemark,
                    fnf_finalized: true,
                    fnf_disbursed: true,
                  });
                  toast.success("FNF marked as Disbursed");
                  load(); onSaved?.();
                } catch (e) { toast.error(formatError(e)); }
              }} className="bg-emerald-600 hover:bg-emerald-700 w-full" disabled={!checklistComplete} data-testid="fnf-mark-disbursed">
                Mark as Disbursed
              </Button>
            ) : (
              <Button variant="outline" onClick={async () => {
                if (!window.confirm("Unmark disbursement? Use only if you marked it disbursed by mistake.")) return;
                try {
                  await api.patch(`/resignations/${resignationId}`, { fnf_disbursed: false, fnf_disbursed_date: "" });
                  load(); onSaved?.();
                } catch (e) { toast.error(formatError(e)); }
              }} className="w-full text-amber-700 border-amber-200">Undo Mark</Button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          {s.fnf_finalized ? (
            <Badge className="bg-emerald-100 text-emerald-800">FNF Finalized</Badge>
          ) : (
            <>
              <Button variant="outline" onClick={() => saveFnf(false)} disabled={finalizing} data-testid="fnf-save">Save</Button>
              <Button onClick={() => saveFnf(true)} disabled={finalizing || !checklistComplete} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fnf-finalize">Finalize FNF</Button>
            </>
          )}
        </div>
      </Section>
    </div>
  );
}

function StatBox({ label, value, tone, icon: Icon, count }) {
  const tones = {
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div className={`rounded-md border-2 p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
        {Icon && <Icon size={14} className="opacity-50" />}
      </div>
      <div className="mono font-extrabold text-xl mt-0.5">{value}</div>
      {count != null && <div className="text-[10px] mt-0.5 opacity-70">{count} item{count === 1 ? "" : "s"}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-violet-600" />} {title}
      </div>
      {children}
    </div>
  );
}

function FNFDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/resignations-fnf-overview")
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const INR = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const filtered = rows.filter((r) => {
    if (filter === "pending") return !r.fnf_disbursed;
    if (filter === "disbursed") return r.fnf_disbursed;
    if (filter === "ready") return !r.fnf_disbursed && r.fnf_finalized;
    return true;
  });

  const total_pending = rows.filter((r) => !r.fnf_disbursed).reduce((s, r) => s + (r.fnf_amount || 0), 0);
  const total_disbursed = rows.filter((r) => r.fnf_disbursed).reduce((s, r) => s + (r.fnf_amount || 0), 0);

  const markDisbursed = async (id, amount) => {
    if (!window.confirm(`Mark FNF of ${INR(amount)} as Disbursed?`)) return;
    try {
      await api.patch(`/resignations/${id}`, { fnf_disbursed: true });
      toast.success("Marked as Disbursed");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="fnf-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Total Resigned" value={rows.length} tone="violet" icon={UserMinus} />
        <StatBox label="FNF Pending" value={INR(total_pending)} tone="amber" icon={Wallet} count={rows.filter((r) => !r.fnf_disbursed).length} />
        <StatBox label="FNF Disbursed" value={INR(total_disbursed)} tone="emerald" icon={IndianRupee} count={rows.filter((r) => r.fnf_disbursed).length} />
        <StatBox label="Ready to Disburse" value={rows.filter((r) => !r.fnf_disbursed && r.fnf_finalized).length} tone="sky" icon={CalendarClock} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {["all", "pending", "ready", "disbursed"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${filter === f ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200 hover:border-violet-300"}`}
            data-testid={`fnf-filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="pk-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/60 border-b border-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 font-semibold">Last Working Day</th>
                <th className="px-4 py-3 font-semibold">FNF Amount</th>
                <th className="px-4 py-3 font-semibold">Planned Disbursement</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400 text-sm">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400 text-sm">No resignations match this filter.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`fnf-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{r.employee_name}</div>
                    <div className="text-[10px] text-slate-400 mono">{r.employee_code}</div>
                  </td>
                  <td className="px-4 py-3 mono text-[11.5px]">
                    {fmtDateStr(r.approved_last_working_day)}
                    {r.lwd_passed && <Badge className="ml-1 bg-slate-100 text-slate-600 text-[9px]">Past</Badge>}
                  </td>
                  <td className="px-4 py-3 mono font-semibold text-emerald-700">{INR(r.fnf_amount)}</td>
                  <td className="px-4 py-3 mono text-[11.5px] text-sky-700">{r.fnf_disburse_planned_date || "—"}</td>
                  <td className="px-4 py-3">
                    {r.fnf_disbursed ? (
                      <Badge className="bg-emerald-100 text-emerald-800">
                        Disbursed {r.fnf_disbursed_date && `· ${r.fnf_disbursed_date}`}
                      </Badge>
                    ) : r.fnf_finalized ? (
                      <Badge className="bg-sky-100 text-sky-800">Ready to Disburse</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800">FNF Pending</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!r.fnf_disbursed && r.fnf_amount > 0 && (
                      <Button size="sm" onClick={() => markDisbursed(r.id, r.fnf_amount)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[11px]" data-testid={`fnf-disburse-${r.id}`}>
                        Mark Disbursed
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function FarewellPanel({ resignationId, viewerId, viewing }) {
  const [list, setList] = useState([]);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const isOwnResignation = viewing?.employee_id === viewerId;

  const load = () => {
    api.get(`/resignations/${resignationId}/farewell-messages`).then(({ data }) => setList(data || []));
  };
  useEffect(load, [resignationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/resignations/${resignationId}/farewell-messages`, { message: msg.trim() });
      setMsg(""); load();
      toast.success("Wishes posted");
    } catch (e) { toast.error(formatError(e)); } finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-violet-50 to-sky-50 border border-violet-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2 text-violet-800">
          <MessageCircle size={16} />
          <div className="font-bold text-[14px]">Farewell Wishes for {viewing.employee_name}</div>
        </div>
        <div className="text-[12px] text-slate-600">
          Last working day · <span className="mono font-semibold">{fmtDateStr(viewing.approved_last_working_day, "—")}</span>
          {viewing.approved_last_working_day && <span className="ml-2 text-[11px] text-slate-500">({viewing.days_remaining} days remaining)</span>}
        </div>
      </div>

      {!isOwnResignation && (
        <div className="border border-slate-200 rounded-md p-3 bg-white">
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Post good wishes for them</Label>
          <Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} className="mt-1.5" placeholder="Wish them well, share a memory, say thanks…" data-testid="farewell-msg" />
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={send} disabled={sending || !msg.trim()} className="bg-violet-600 hover:bg-violet-700" data-testid="farewell-send">
              <Send size={12} className="mr-1" /> Post
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {list.length === 0 ? (
          <div className="text-slate-400 text-[12px] text-center py-6 italic">No messages yet. Be the first to post good wishes.</div>
        ) : (
          list.map((m) => (
            <div key={m.id} className="border border-slate-100 rounded p-3 bg-white" data-testid={`farewell-${m.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-slate-800 text-[12.5px]">{m.author_name}</div>
                <div className="text-[10px] text-slate-400 mono">{new Date(m.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <div className="text-slate-700 text-[13px] whitespace-pre-wrap">{m.message}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


// ====== Documents Panel ======
const DOC_TYPES = [
  { key: "exit_clearance", label: "No Due / Exit Clearance Certificate" },
  { key: "relieving_letter", label: "Relieving Letter" },
  { key: "experience_letter", label: "Experience Letter" },
  { key: "fnf_statement", label: "Full & Final Settlement Statement" },
  { key: "service_certificate", label: "Service Certificate" },
];

function DocumentsPanel({ resignationId, employeeName }) {
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/resignations/${resignationId}/documents`);
      setCatalog(data.catalog || []);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, [resignationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (docType) => {
    setBusy(`gen-${docType}`);
    try {
      await api.post(`/resignations/${resignationId}/documents/${docType}/generate`);
      toast.success(`Generated ${docType.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };

  const upload = async (docType, file) => {
    if (!file) return;
    setBusy(`up-${docType}`);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/resignations/${resignationId}/documents/${docType}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Signed copy uploaded");
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };

  const download = async (docType, doc) => {
    try {
      const resp = await api.get(`/resignations/${resignationId}/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `${docType}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (doc) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      await api.delete(`/resignations/${resignationId}/documents/${doc.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="docs-panel">
      <div className="text-[12px] text-slate-600">
        Auto-generate the standard offboarding documents for <b>{employeeName}</b> using the company
        letterhead. You can also upload a signed copy of any document — it will be visible to the
        ex-employee via their temporary access.
      </div>
      <div className="space-y-2.5">
        {DOC_TYPES.map((dt) => {
          const row = catalog.find((c) => c.doc_type === dt.key);
          const latest = row?.latest;
          return (
            <div key={dt.key} className="border border-slate-200 rounded-md p-3 bg-white flex items-center justify-between gap-3 flex-wrap" data-testid={`doc-row-${dt.key}`}>
              <div className="flex items-start gap-2 min-w-0 flex-1">
                <FileText size={16} className="text-violet-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 text-[13.5px]">{dt.label}</div>
                  {latest ? (
                    <div className="text-[10.5px] text-slate-500 mono mt-0.5 truncate">
                      {latest.kind === "uploaded" ? "Signed" : "Generated"} · v{latest.version} · {latest.filename}
                    </div>
                  ) : (
                    <div className="text-[10.5px] text-slate-400 italic mt-0.5">Not generated yet</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => generate(dt.key)}
                  disabled={busy === `gen-${dt.key}`}
                  className="h-8 text-[11px]"
                  data-testid={`doc-generate-${dt.key}`}
                >
                  <RefreshCw size={11} className="mr-1" />
                  {latest ? "Regenerate" : "Generate"}
                </Button>
                <label className="inline-flex items-center cursor-pointer">
                  <Button
                    size="sm"
                    variant="outline"
                    asChild
                    className="h-8 text-[11px]"
                    data-testid={`doc-upload-${dt.key}`}
                  >
                    <span>
                      <Upload size={11} className="mr-1" /> Upload Signed
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    className="hidden"
                    onChange={(e) => { upload(dt.key, e.target.files?.[0]); e.target.value = ""; }}
                  />
                </label>
                {latest && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => download(dt.key, latest)}
                      className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700"
                      data-testid={`doc-download-${dt.key}`}
                    >
                      <Download size={11} className="mr-1" /> Download
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(latest)}
                      className="h-8 text-[11px] text-rose-700 hover:bg-rose-50"
                      data-testid={`doc-delete-${dt.key}`}
                    >
                      <Trash2 size={11} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ====== Resigned Employees Panel (Temp Credentials) ======
function ResignedEmployeesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/offboarding/resigned-employees");
      setRows(data || []);
    } catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (rid) => {
    if (!window.confirm("Generate a new temporary password? Any existing password will be replaced.")) return;
    setBusy(rid);
    try {
      const { data } = await api.post(`/resignations/${rid}/temp-access/generate`);
      toast.success("Temporary credentials generated");
      // copy to clipboard
      try {
        await navigator.clipboard.writeText(`Email: ${data.temp_email}\nPassword: ${data.temp_password}\nExpires: ${data.expires_at}`);
        toast.message("Credentials copied to clipboard");
      } catch { /* ignore */ }
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };

  const revokeFor = async (rid) => {
    if (!window.confirm("Revoke this employee's temporary access? They will not be able to log in.")) return;
    setBusy(rid);
    try {
      await api.post(`/resignations/${rid}/temp-access/revoke`);
      toast.success("Temporary access revoked");
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="resigned-employees-panel">
      <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] text-violet-800">
        Resigned employees can be granted a 30-day temporary credential after their Last Working Day
        to log in and download their offboarding documents. Only HR &amp; Master Admin can view or
        regenerate these credentials.
      </div>
      <div className="pk-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/60 border-b border-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 font-semibold">Last Working Day</th>
                <th className="px-4 py-3 font-semibold">Temp Email</th>
                <th className="px-4 py-3 font-semibold">Temp Password</th>
                <th className="px-4 py-3 font-semibold">Access Expires</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400 text-sm">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm"><Inbox className="mx-auto mb-2" size={20} /> No approved resignations yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`resigned-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{r.employee_name}</div>
                    <div className="text-[10px] text-slate-400 mono">{r.employee_code}</div>
                  </td>
                  <td className="px-4 py-3 mono text-[11.5px]">
                    {fmtDateStr(r.approved_last_working_day, "—")}
                    {r.lwd_passed && <Badge className="ml-1 bg-slate-100 text-slate-600 text-[9px]">Past</Badge>}
                  </td>
                  <td className="px-4 py-3 mono text-[11px] text-slate-700">{r.temp_email || "—"}</td>
                  <td className="px-4 py-3 mono text-[11px]">
                    {r.temp_password_plain ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-rose-700">
                          {reveal[r.id] ? r.temp_password_plain : "•".repeat(r.temp_password_plain.length)}
                        </span>
                        <button
                          onClick={() => setReveal({ ...reveal, [r.id]: !reveal[r.id] })}
                          className="text-slate-500 hover:text-violet-700"
                          data-testid={`reveal-pw-${r.id}`}
                          title={reveal[r.id] ? "Hide" : "Reveal"}
                        >
                          {reveal[r.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                        </button>
                        <button
                          onClick={async () => {
                            try { await navigator.clipboard.writeText(r.temp_password_plain); toast.success("Copied"); } catch { /* ignore */ }
                          }}
                          className="text-[10px] text-violet-700 hover:underline"
                        >
                          copy
                        </button>
                      </div>
                    ) : <span className="text-slate-400 italic">—</span>}
                  </td>
                  <td className="px-4 py-3 mono text-[11.5px]">
                    {fmtDateStr(r.temp_access_expires_at, "—")}
                  </td>
                  <td className="px-4 py-3">
                    {r.temp_access_active ? (
                      <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                    ) : r.temp_access_enabled ? (
                      <Badge className="bg-amber-100 text-amber-800">Expired</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">Not Generated</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => generate(r.id)}
                        disabled={busy === r.id}
                        className="h-8 text-[11px]"
                        data-testid={`gen-temp-${r.id}`}
                      >
                        <KeyRound size={11} className="mr-1" />
                        {r.temp_access_enabled ? "Regenerate" : "Generate"}
                      </Button>
                      {r.temp_access_enabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => revokeFor(r.id)}
                          disabled={busy === r.id}
                          className="h-8 text-[11px] text-rose-700 hover:bg-rose-50"
                          data-testid={`revoke-temp-${r.id}`}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ====== Letterhead Settings Panel ======
export function LetterheadPanel({ isMaster }) {
  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [margins, setMargins] = useState({ top: 5, bottom: 3, left: 2, right: 2 });
  const [file, setFile] = useState(null);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setCompanies(data || []);
      if (data && data.length && !selectedId) setSelectedId(data[0].id);
      else if (selectedId) {
        const cur = (data || []).find((c) => c.id === selectedId);
        if (cur?.margins_cm) setMargins(cur.margins_cm);
      }
    } catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = companies.find((c) => c.id === selectedId);

  // Refresh margins state when switching company
  useEffect(() => {
    if (selected?.margins_cm) setMargins(selected.margins_cm);
    setFile(null);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("margin_top", String(margins.top));
      fd.append("margin_bottom", String(margins.bottom));
      fd.append("margin_left", String(margins.left));
      fd.append("margin_right", String(margins.right));
      if (file) fd.append("file", file);
      await api.post(`/companies/${selected.id}/letterhead`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Letterhead saved for ${selected.name}`);
      setFile(null);
      await load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  const removeLh = async () => {
    if (!selected) return;
    if (!window.confirm(`Remove letterhead PDF for ${selected.name}?`)) return;
    try {
      await api.delete(`/companies/${selected.id}/letterhead`);
      toast.success("Letterhead removed");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const addCompany = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Name required"); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/companies", { name });
      toast.success(`Added '${data.name}'`);
      setNewName("");
      await load();
      setSelectedId(data.id);
    } catch (e) { toast.error(formatError(e)); } finally { setAdding(false); }
  };

  const deleteCompany = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete company '${selected.name}'? Default companies are deactivated; custom ones are permanently removed.`)) return;
    try {
      await api.delete(`/companies/${selected.id}`);
      toast.success("Company removed");
      setSelectedId("");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const preview = () => {
    if (!selected?.has_letterhead) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/companies/${selected.id}/letterhead-preview`, "_blank");
  };

  if (loading) return <div className="text-slate-400 text-sm py-6 text-center">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="letterhead-panel">
      <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2.5 text-[12px] text-sky-800">
        Upload a PDF letterhead for each company. Generated offboarding documents (Relieving Letter,
        FNF Statement, etc.) will use page 1 of the PDF as the background and lay out the content
        within your configured margins (in centimetres). Only the Master Admin can change
        letterhead settings.
      </div>

      {/* Company chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="lh-company-chips">
        {companies.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelectedId(c.id)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border inline-flex items-center gap-1.5 ${
              selectedId === c.id
                ? "bg-violet-600 text-white border-violet-600"
                : "bg-white text-slate-700 border-slate-200 hover:border-violet-300"
            }`}
            data-testid={`lh-chip-${c.id}`}
          >
            {c.name}
            {c.has_letterhead ? (
              <span className={`w-1.5 h-1.5 rounded-full ${selectedId === c.id ? "bg-emerald-300" : "bg-emerald-500"}`} />
            ) : (
              <span className={`w-1.5 h-1.5 rounded-full ${selectedId === c.id ? "bg-rose-300" : "bg-rose-400"}`} />
            )}
          </button>
        ))}
      </div>

      {/* Add company */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Add Company</Label>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Panchakosh Ventures"
            className="mt-1.5 h-9"
            data-testid="lh-new-company-name"
          />
        </div>
        <Button
          size="sm"
          onClick={addCompany}
          disabled={adding}
          className="bg-violet-600 hover:bg-violet-700 h-9"
          data-testid="lh-add-company"
        >
          {adding ? "Adding…" : "Add Company"}
        </Button>
      </div>

      {/* Selected company details */}
      {!selected ? (
        <div className="text-slate-400 text-sm py-6 text-center italic">Select or add a company to configure its letterhead.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="lh-detail">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="heading text-lg font-bold text-slate-900">{selected.name}</div>
              <div className="text-[10.5px] text-slate-500">{selected.is_default ? "Default company" : "Custom company"}</div>
            </div>
            <div className="flex items-center gap-1.5">
              {selected.has_letterhead && (
                <Button size="sm" variant="outline" onClick={preview} className="h-8 text-[11px]" data-testid="lh-preview">
                  <Download size={11} className="mr-1" /> Preview PDF
                </Button>
              )}
              {isMaster && selected.has_letterhead && (
                <Button size="sm" variant="ghost" onClick={removeLh} className="h-8 text-[11px] text-rose-700 hover:bg-rose-50">
                  <Trash2 size={11} className="mr-1" /> Remove Letterhead
                </Button>
              )}
              {isMaster && (
                <Button size="sm" variant="ghost" onClick={deleteCompany} className="h-8 text-[11px] text-rose-700 hover:bg-rose-50" data-testid="lh-delete-company">
                  <Trash2 size={11} className="mr-1" /> Delete Company
                </Button>
              )}
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Letterhead PDF</Label>
            <div className="mt-1.5 flex items-center gap-3 flex-wrap">
              {selected.has_letterhead ? (
                <Badge className="bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
                  <FileText size={11} /> Uploaded · {selected.letterhead_filename || "letterhead.pdf"}
                </Badge>
              ) : (
                <Badge className="bg-rose-100 text-rose-700">No letterhead uploaded</Badge>
              )}
              {isMaster && (
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="lh-pdf-input"
                  className="text-[11px]"
                />
              )}
            </div>
            {file && <div className="text-[10.5px] text-emerald-700 mt-1">Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
            <div className="text-[10.5px] text-slate-500 mt-1">
              Upload a single-page PDF designed as your official company letterhead. The first page
              is used as the background; document content is drawn on top, inside the margins below.
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
              Content Margins (cm) — area where document text is rendered
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5">
              {["top", "bottom", "left", "right"].map((side) => (
                <div key={side}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{side}</div>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="20"
                    value={margins[side]}
                    onChange={(e) => setMargins({ ...margins, [side]: parseFloat(e.target.value) || 0 })}
                    disabled={!isMaster}
                    className="mono h-9 text-center font-semibold"
                    data-testid={`lh-margin-${side}`}
                  />
                </div>
              ))}
            </div>
            <div className="text-[10.5px] text-slate-500 mt-1">
              Tip: A4 page is 21.0 × 29.7 cm. For most letterheads, Top ≈ 5 cm (logo block), Bottom ≈ 3 cm (footer), Left/Right ≈ 2 cm.
            </div>
          </div>

          {isMaster && (
            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={busy}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="lh-save"
              >
                {busy ? "Saving…" : selected.has_letterhead ? "Update Letterhead & Margins" : "Save Letterhead & Margins"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
